import React, { useState, useContext } from 'react';
import styles from './styles.module.css';
import { AlertMessagesContext } from 'react-alert-messages';
import { useEffect } from 'react';
import DataTable from 'components/utils/DataTable/index';
import Info from 'components/utils/Alert/Info';
import { Button, TextField, Grid } from '@mui/material';
import OrderActionService from 'services/OrderActionService';
import EditOrderActionDialog from 'components/popups/EditOrderActionDialog.js';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import NoteAddIcon from '@mui/icons-material/NoteAdd';

function OrderActions() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [orderActions, setOrderActions] = useState([]);
  const [newOrderAction, setNewOrderAction] = useState('');
  const [selectedOrderActionForEdit, setSelectedOrderActionForEdit] = useState(null);
  const [order, setOrder] = useState();

  const getOrderActions = async () => {
    setLoadingIndicator(true);
    try {
      const res = await OrderActionService.getOrderActions();
      setOrderActions(res);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const updateOrderActionStatus = (action) => {
    const data = { is_active: !action?.is_active };
    updateOrderActions(action, data);
  };

  const updateOrderActions = async (action, data) => {
    setLoadingIndicator(true);
    try {
      await OrderActionService.updateOrderAction(action?.id, data);
      postAlertMessage({
        text: tManagement('Order actions updated successfully'),
        type: 'success',
      });
      getOrderActions();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const createOrderAction = async () => {
    setLoadingIndicator(true);
    try {
      if (!newOrderAction.length) {
        postAlertMessage({
          text: tManagement('Please provide a valid order action name'),
          type: 'failed',
        });
        return;
      }
      await OrderActionService.createOrderAction({ action: newOrderAction, order: Number(order) });
      postAlertMessage({
        text: tManagement('Order actions added successfully'),
        type: 'success',
      });
      setNewOrderAction('');
      getOrderActions();
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const headerData = [
    {
      label: tCommon('Action'),
      id: 'action',
      type: 'text',
    },
    {
      label: tCommon('Order'),
      id: 'order',
      type: 'text',
    },
    {
      label: tCommon('Status'),
      id: 'staus',
      type: 'callback',
      viewRender: (obj) => {
        return obj.is_active ? tCommon('Active') : tCommon('Deactive');
      },
    },
    {
      label: tCommon('Edit'),
      id: 'edit',
      type: 'button',
      title: tCommon('Edit'),
      clickHandler: setSelectedOrderActionForEdit,
    },
    {
      id: 'deleteBtn',
      type: 'callback',
      align: 'right',
      viewRender: (obj) => (
        <Button
          onClick={(e) => {
            updateOrderActionStatus(obj);
            e.stopPropagation();
          }}
          variant="contained"
          size="small"
          color="primary"
          style={{ width: 105 }}
        >
          {obj.is_active ? tCommon('Deactivate') : tCommon('Activate')}
        </Button>
      ),
    },
  ];

  const getRowStyle = (row) => {
    if (row.is_active) {
      return;
    }
    return { backgroundColor: 'rgba(245, 169, 39, 0.2)' };
  };

  useEffect(() => {
    getOrderActions();
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Create Order Actions')}</span>
        </div>
        <Grid container mb={2} spacing={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              color="primary"
              label={tCommon('Enter order action name')}
              inputProps={{
                style: {
                  padding: 10,
                },
              }}
              value={newOrderAction}
              onChange={(e) => setNewOrderAction(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4} textAlign={'end'}>
            <NumberField
              color="primary"
              label={tCommon('Enter order')}
              inputProps={{
                style: {
                  padding: 10,
                },
              }}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              style={{ backgroundColor: '#ff851b', padding: 9 }}
              onClick={createOrderAction}
            >
              <NoteAddIcon className={styles.actionBtnIcon} />
              {tCommon('Create')}
            </Button>
          </Grid>
        </Grid>
        {loadingIndicator || orderActions.length ? (
          <DataTable
            columns={headerData}
            rows={orderActions || []}
            isLoading={loadingIndicator}
            rowStyle={getRowStyle}
          />
        ) : (
          !loadingIndicator && (
            <Info
              title={tManagement('No order actions to list')}
              content={tManagement('You have no order actions to list. Please create a new order action')}
            />
          )
        )}
      </div>
      {selectedOrderActionForEdit && (
        <EditOrderActionDialog
          handleClose={() => {
            setSelectedOrderActionForEdit(null);
          }}
          orderAction={selectedOrderActionForEdit}
          updateOrderAction={updateOrderActions}
        />
      )}
    </div>
  );
}

export default OrderActions;
